"use client";

import { useEffect, useMemo, useState } from "react";
import {
  IconChevronDown,
  IconChevronUp,
  IconMinus,
  IconPlus,
} from "@/lib/icons";
import Container from "../container";
import SafeHtml from "../CMS/SafeHtml";
import PageSchema from "@/components/SEO/PageSchema";
import { buildFaqPageSchema } from "@/lib/faqSchema";
import styles from "./faqSection.module.css";

const INITIAL_VISIBLE_COUNT = 6;
const INITIALLY_OPEN_COUNT = 0;
const WIDE_ONLY_INDEX = 1;
const STACKED_QUERY = "(max-width: 767px)";

import {
  resolveDynamicVariables,
  buildDynamicContext,
} from "@/lib/replaceDynamicValues";

export function extractFaqItems(data) {
  if (!data) return [];
  if (Array.isArray(data?.faqs)) return data.faqs;
  if (Array.isArray(data?.faq)) return data.faq;
  if (Array.isArray(data?.faq_list)) return data.faq_list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;
  if (typeof data === "object") {
    // Check if any property in data is an array containing question items (e.g. data["1"] = [...])
    for (const val of Object.values(data)) {
      if (Array.isArray(val) && val.some((item) => item?.question)) {
        return val;
      }
    }
    const objValues = Object.values(data).filter(
      (item) => item && typeof item === "object" && item.question,
    );
    if (objValues.length > 0) return objValues;
  }
  return [];
}

export default function FAQSection({
  data,
  inArticle = false,
  className = "",
  context,
}) {
  const dynamicContext = context || buildDynamicContext();
  const resolvedData = useMemo(() => {
    return resolveDynamicVariables(data, dynamicContext);
  }, [data, dynamicContext]);

  const allFaqs = useMemo(() => {
    return extractFaqItems(resolvedData).filter(
      (faq) => faq?.question && faq?.answer,
    );
  }, [resolvedData]);

  // The first answers start open so they're visible in the page as
  // rendered, not only present in the HTML behind a click: AI engines and
  // Google give visible text more weight than collapsed content. Each item
  // opens and closes independently.
  const [openItems, setOpenItems] = useState(
    () => new Set(Array.from({ length: INITIALLY_OPEN_COUNT }, (_, i) => i)),
  );
  // The server can't know the layout, so the second answer is rendered open
  // with `wideOnlyOpen`, which CSS shows as closed when the cards stack.
  // After mount the state catches up (closed when stacked) — nothing moves
  // on screen, and aria-expanded then matches what's visible.
  const [wideOnlyPending, setWideOnlyPending] = useState(true);
  useEffect(() => {
    if (window.matchMedia(STACKED_QUERY).matches) {
      setOpenItems((prev) => {
        const next = new Set(prev);
        next.delete(WIDE_ONLY_INDEX);
        return next;
      });
    }
    setWideOnlyPending(false);
  }, []);
  const [isExpanded, setIsExpanded] = useState(false);

  // Built from the same array the list below renders, so the markup can't
  // drift from the visible questions the way the hand-authored CMS block did.
  const faqSchema = useMemo(() => buildFaqPageSchema(allFaqs), [allFaqs]);

  if (allFaqs.length === 0) return null;

  const title = resolvedData?.title || "Frequently Asked Questions";

  // Every question renders; the ones past the initial count are collapsed with
  // CSS rather than sliced out of the array. Slicing kept them out of the
  // server-rendered HTML entirely, so a crawler only ever saw the first six.
  const hasMore = allFaqs.length > INITIAL_VISIBLE_COUNT;

  const toggleItem = (index) => {
    setOpenItems((prev) => {
      if (prev.has(index)) return new Set();
      return new Set([index]);
    });
  };

  const faqContent = (
    <>
      {/* Header Section */}
      <div className={inArticle ? styles.blogHeaderSection : styles.headerSection}>
        <div className={inArticle ? styles.blogTitleRow : styles.titleRow}>
          <div className={inArticle ? styles.blogTitleWrap : styles.titleWrap}>
            <h2 className={inArticle ? styles.blogTitle : styles.title}>{title}</h2>
          </div>
          <span className={inArticle ? styles.blogCountBadge : styles.countBadge}>
            {allFaqs.length} {allFaqs.length === 1 ? "question" : "questions"}
          </span>
        </div>
      </div>

      {/* FAQs Grid */}
      <div className={inArticle ? styles.blogGrid : styles.faqGrid}>
        {allFaqs.map((faq, index) => {
          const isOpen = openItems.has(index);
          const isWideOnly =
            wideOnlyPending && index === WIDE_ONLY_INDEX && isOpen;
          const isBeyondInitial = !isExpanded && index >= INITIAL_VISIBLE_COUNT;
          const panelId = `faq-answer-${index}`;
          return (
            <div
              key={index}
              className={`${inArticle ? styles.blogCard : styles.faqCard} ${
                isWideOnly
                  ? styles.wideOnlyOpen
                  : isOpen
                    ? inArticle ? styles.blogCardActive : styles.faqCardActive
                    : ""
              }`}
              hidden={isBeyondInitial}
            >
              <button
                type="button"
                className={inArticle ? styles.blogHeaderBtn : styles.faqHeader}
                onClick={() => toggleItem(index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
              >
                <span className={inArticle ? styles.blogQuestion : styles.question}>{faq.question}</span>
                <span className={inArticle ? styles.blogExpandIcon : styles.expandIcon}>
                  {isWideOnly ? (
                    <>
                      <IconMinus size={15} className={styles.iconWide} />
                      <IconPlus size={15} className={styles.iconStacked} />
                    </>
                  ) : isOpen ? (
                    <IconMinus size={15} />
                  ) : (
                    <IconPlus size={15} />
                  )}
                </span>
              </button>

              {/* Rendered always and hidden when closed, rather than mounted on
                  open: an unmounted answer is absent from the DOM, so no
                  crawler or AI engine could read it before a click. */}
              <div id={panelId} className={inArticle ? styles.blogBody : styles.faqBody} hidden={!isOpen}>
                <SafeHtml html={faq.answer} className={inArticle ? styles.blogAnswer : styles.answer} />
              </div>
            </div>
          );
        })}
      </div>

      {/* View All / Show Less Toggle Button */}
      {hasMore && (
        <div className={styles.toggleWrapper}>
          <button
            type="button"
            className={inArticle ? styles.blogToggleButton : styles.toggleButton}
            onClick={() => {
              setIsExpanded((prev) => !prev);
              setOpenItems(new Set());
            }}
            aria-expanded={isExpanded}
          >
            <span>
              {isExpanded
                ? "Show less FAQs"
                : `View all ${allFaqs.length} FAQs`}
            </span>
            {isExpanded ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )}
          </button>
        </div>
      )}
    </>
  );

  if (inArticle) {
    return (
      <>
        <PageSchema schemas={[faqSchema]} idPrefix="faq-schema" />
        <div className={`${styles.inArticleWrapper} ${className}`}>
          {faqContent}
        </div>
      </>
    );
  }

  return (
    <>
      <PageSchema schemas={[faqSchema]} idPrefix="faq-schema" />
      <section
        className={`${styles.section} ${className}`}
        aria-label="Frequently Asked Questions"
      >
        <Container>{faqContent}</Container>
      </section>
    </>
  );
}
